import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBlackaChat } from '../../hooks/useBlackaChat';
import { BLACKA_CHAT_ENABLED } from '../../config/blackaChat';
import type { ChatMessage } from '../../types/chat';
import { isSafeExternalUrl, isSafeRelativePath, normalizeChatLink } from '../../utils/blackaChatLinks';
import { renderMarkdownBoldSegments } from '../../utils/blackaMessageFormat';
import './BlackaChatWidget.css';

const MARKDOWN_LINK = /\[([^\]]+)\]\(([^)]+)\)/g;

function isEscapeKey(event: { key: string; code?: string }): boolean {
  return event.key === 'Escape' || event.key === 'Esc' || event.code === 'Escape';
}

function renderMessageContent(content: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  const pushTextSegment = (segment: string, segmentKey: number) => {
    parts.push(...renderMarkdownBoldSegments(segment, `seg-${segmentKey}`));
  };

  const regex = new RegExp(MARKDOWN_LINK.source, 'g');
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      pushTextSegment(content.slice(lastIndex, match.index), key);
    }

    const label = match[1];
    const href = normalizeChatLink(match[2], window.location.origin);

    if (isSafeRelativePath(href)) {
      parts.push(
        <Link key={key} to={href}>
          {label}
        </Link>
      );
    } else if (isSafeExternalUrl(href)) {
      parts.push(
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      );
    } else {
      parts.push(label);
    }

    key += 1;
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < content.length) {
    pushTextSegment(content.slice(lastIndex), key);
  }

  return parts.length ? parts : [content];
}

function MessageBubble({ message }: { message: ChatMessage }) {
  return (
    <div className={`blacka-chat-bubble ${message.role}`}>
      {renderMessageContent(message.content)}
    </div>
  );
}

export default function BlackaChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const { messages, loading, error, sendMessage, clearChat } = useBlackaChat();
  const panelRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const openRef = useRef(false);
  openRef.current = open;

  const closePanel = useCallback(() => setOpen(false), []);

  const handleGlobalEscape = useCallback((event: KeyboardEvent) => {
    if (!openRef.current || !isEscapeKey(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    closePanel();
  }, [closePanel]);

  const handlePanelKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isEscapeKey(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      closePanel();
    },
    [closePanel]
  );

  useEffect(() => {
    if (!BLACKA_CHAT_ENABLED) {
      return undefined;
    }

    window.addEventListener('keydown', handleGlobalEscape, true);
    document.addEventListener('keydown', handleGlobalEscape, true);
    window.addEventListener('keyup', handleGlobalEscape, true);
    document.addEventListener('keyup', handleGlobalEscape, true);

    return () => {
      window.removeEventListener('keydown', handleGlobalEscape, true);
      document.removeEventListener('keydown', handleGlobalEscape, true);
      window.removeEventListener('keyup', handleGlobalEscape, true);
      document.removeEventListener('keyup', handleGlobalEscape, true);
    };
  }, [handleGlobalEscape]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    panelRef.current?.focus();
    const frameId = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => cancelAnimationFrame(frameId);
  }, [open]);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [open, messages, loading, error]);

  if (!BLACKA_CHAT_ENABLED) {
    return null;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const text = input;
    setInput('');
    sendMessage(text);
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          className="blacka-chat-launcher"
          onClick={() => setOpen(true)}
          aria-label="Open Blacka chat assistant"
          title="Chat with Blacka"
        >
          <i className="fas fa-comments" aria-hidden />
        </button>
      )}

      {open && (
        <div
          ref={panelRef}
          className="blacka-chat-panel"
          data-testid="blacka-chat-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Blacka chat"
          tabIndex={-1}
          onKeyDown={handlePanelKeyDown}
        >
          <div className="blacka-chat-header">
            <div>
              <p className="blacka-chat-header-title">Blacka</p>
              <p className="blacka-chat-header-sub">Pack My Cart helper · Jamaican Creole</p>
            </div>
            <div className="blacka-chat-header-actions d-flex gap-1">
              <button type="button" onClick={clearChat} aria-label="Clear chat">
                Clear
              </button>
              <button type="button" onClick={closePanel} aria-label="Close chat">
                <i className="fas fa-times" aria-hidden />
              </button>
            </div>
          </div>

          {error && (
            <div className="blacka-chat-error-banner" role="alert" aria-live="assertive">
              {error}
            </div>
          )}

          <div className="blacka-chat-messages" aria-live="polite" aria-relevant="additions text">
            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} />
            ))}
            {loading && <div className="blacka-chat-typing">Blacka a think...</div>}
            <div ref={messagesEndRef} />
          </div>

          <form className="blacka-chat-input-row" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask bout products, checkout, delivery..."
              disabled={loading}
              aria-label="Message to Blacka"
              maxLength={2000}
            />
            <button type="submit" className="blacka-chat-send" disabled={loading || !input.trim()}>
              Send
            </button>
          </form>
        </div>
      )}
    </>
  );
}
